import { UserRole } from '@teetsh/app/src/containers/Demarrage/types';

export type ScholarSystemValue = 'FR' | 'BE' | 'US' | 'AU';

// These keys can't be extracted from i18n files because of admin dependencies
type RoleTranslationKey =
  | 'role.enseignant1erDegre'
  | 'role.enseignant1erDegreBE'
  | 'role.enseignant2ndDegre'
  | 'role.enseignant2ndDegreBE'
  | 'role.etudiantCRPE'
  | 'role.etudiant'
  | 'role.director'
  | 'role.formateur';

export const DEFAULT_CYCLE: Cycle = 'Cycle 2' as const;
const Cycles = ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'] as const;
const NiveauxBe = [
  'M1',
  'M2',
  'M3',
  'P1',
  'P2',
  'P3',
  'P4',
  'P5',
  'P6',
] as const;
const NiveauxFr = [
  'TPS',
  'PS',
  'MS',
  'GS',
  'CP',
  'CE1',
  'CE2',
  'CM1',
  'CM2',
  '6e',
  '5e',
  '4e',
  '3e',
  'ULIS',
  'SEGPA',
] as const;
const NiveauxUs = [
  'TYPK',
  'YPK',
  'PK',
  'K',
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
] as const;

const NiveauxAu = [
  'Pre-foundation',
  'Foundation',
  'Year 1',
  'Year 2',
  'Year 3',
  'Year 4',
  'Year 5',
  'Year 6',
  'Year 7',
  'Year 8',
  'Year 9',
];
const Niveaux = [
  ...NiveauxFr,
  ...NiveauxBe,
  ...NiveauxUs,
  ...NiveauxAu,
] as const;

export type NiveauValue = (typeof Niveaux)[number];
export type Cycle = (typeof Cycles)[number];

type RoleConfig = {
  value: UserRole;
  label?: string;
  translationKey?: RoleTranslationKey;
};

export type ScholarSystem = {
  niveaux: Niveau[];
  cycles: Cycle[];
  defaultNiveau: NiveauValue;
  roles: RoleConfig[];
};
type Niveau = {
  niveau: NiveauValue;
  cycle?: Cycle;
  category?: string;
};

export const ScholarSystems: {
  [K in ScholarSystemValue]: ScholarSystem;
} = {
  FR: {
    niveaux: [
      {
        niveau: 'TPS',
        cycle: 'Cycle 1',
        category: 'Maternelle',
      },
      { niveau: 'PS', cycle: 'Cycle 1', category: 'Maternelle' },
      { niveau: 'MS', cycle: 'Cycle 1', category: 'Maternelle' },
      { niveau: 'GS', cycle: 'Cycle 1', category: 'Maternelle' },
      {
        niveau: 'CP',
        cycle: 'Cycle 2',
        category: 'Élémentaire',
      },
      {
        niveau: 'CE1',
        cycle: 'Cycle 2',
        category: 'Élémentaire',
      },
      {
        niveau: 'CE2',
        cycle: 'Cycle 2',
        category: 'Élémentaire',
      },
      {
        niveau: 'CM1',
        cycle: 'Cycle 3',
        category: 'Élémentaire',
      },
      {
        niveau: 'CM2',
        cycle: 'Cycle 3',
        category: 'Élémentaire',
      },
      {
        niveau: 'ULIS',
        cycle: 'Cycle 2',
        category: 'Dispositif',
      },
      {
        niveau: 'SEGPA',
        cycle: 'Cycle 2',
        category: 'Dispositif',
      },
      { niveau: '6e', cycle: 'Cycle 3', category: 'Collège' },
      { niveau: '5e', cycle: 'Cycle 4', category: 'Collège' },
      { niveau: '4e', cycle: 'Cycle 4', category: 'Collège' },
      { niveau: '3e', cycle: 'Cycle 4', category: 'Collège' },
    ],
    cycles: ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'],
    defaultNiveau: 'CP',
    roles: [
      {
        value: '1er degré',
        translationKey: 'role.enseignant1erDegre',
      },
      { value: 'directeur', translationKey: 'role.director' },
      {
        value: 'etudiant crpe',
        translationKey: 'role.etudiantCRPE',
      },
      { value: 'formateur', translationKey: 'role.formateur' },
      {
        value: '2nd degré',
        translationKey: 'role.enseignant2ndDegre',
      },
    ],
  },
  BE: {
    niveaux: [
      { niveau: 'M1' },
      { niveau: 'M2' },
      { niveau: 'M3' },
      { niveau: 'P1' },
      { niveau: 'P2' },
      { niveau: 'P3' },
      { niveau: 'P4' },
      { niveau: 'P5' },
      { niveau: 'P6' },
    ],
    cycles: ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'], // assign cycles to BE system for compatibility
    defaultNiveau: 'P1',
    roles: [
      {
        value: '1er degré',
        translationKey: 'role.enseignant1erDegreBE',
      },
      { value: 'directeur', translationKey: 'role.director' },
      { value: 'etudiant crpe', translationKey: 'role.etudiant' },
      { value: 'formateur', translationKey: 'role.formateur' },
      {
        value: '2nd degré',
        translationKey: 'role.enseignant2ndDegreBE',
      },
    ],
  },
  US: {
    niveaux: [
      { niveau: 'TYPK', cycle: 'Cycle 1' },
      { niveau: 'YPK', cycle: 'Cycle 1' },
      { niveau: 'PK', cycle: 'Cycle 1' },
      { niveau: 'K', cycle: 'Cycle 1' },
      { niveau: 'Grade 1', cycle: 'Cycle 2' },
      { niveau: 'Grade 2', cycle: 'Cycle 2' },
      { niveau: 'Grade 3', cycle: 'Cycle 2' },
      { niveau: 'Grade 4', cycle: 'Cycle 3' },
      { niveau: 'Grade 5', cycle: 'Cycle 3' },
    ],
    cycles: ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'], // assign cycles to US system for compatibility
    defaultNiveau: 'Grade 1',
    roles: [
      {
        value: '1er degré',
        translationKey: 'role.enseignant1erDegre',
      },
      { value: 'directeur', translationKey: 'role.director' },
      {
        value: 'etudiant crpe',
        translationKey: 'role.etudiantCRPE',
      },
      { value: 'formateur', translationKey: 'role.formateur' },
      {
        value: '2nd degré',
        translationKey: 'role.enseignant2ndDegre',
      },
    ],
  },
  AU: {
    niveaux: [
      { niveau: 'Pre-foundation', cycle: 'Cycle 1' },
      { niveau: 'Foundation', cycle: 'Cycle 1' },
      { niveau: 'Year 1', cycle: 'Cycle 2' },
      { niveau: 'Year 2', cycle: 'Cycle 2' },
      { niveau: 'Year 3', cycle: 'Cycle 2' },
      { niveau: 'Year 4', cycle: 'Cycle 3' },
      { niveau: 'Year 5', cycle: 'Cycle 3' },
      { niveau: 'Year 6', cycle: 'Cycle 3' },
    ],
    cycles: ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'], // assign cycles to US system for compatibility
    defaultNiveau: 'Year 1',
    roles: [
      { value: '1er degré', label: 'Primary School teacher' },
      { value: 'etudiant crpe', label: 'Pre-service teacher' },
      { value: 'directeur', label: 'Principal' },
      { value: '2nd degré', label: 'Secondary school teacher' },
    ],
  },
};

export function getNiveauFromScholarSystem(
  scholarSystem: ScholarSystem,
  niveau?: string,
): Niveau | undefined {
  return scholarSystem.niveaux.find(n => n.niveau === niveau);
}

export function getNiveauFromScholarSystemOrDefault(
  scholarSystem: ScholarSystem,
  niveau?: string,
): Niveau {
  const defaultNiveau: Niveau = {
    niveau: scholarSystem.defaultNiveau,
    cycle:
      getNiveauFromScholarSystem(scholarSystem, scholarSystem.defaultNiveau)
        ?.cycle || DEFAULT_CYCLE,
  };

  return getNiveauFromScholarSystem(scholarSystem, niveau) || defaultNiveau;
}

export function useRolesForScholarSystem(
  scholarSystemValue: ScholarSystemValue,
  t: (key: RoleTranslationKey) => string,
) {
  const scholarSystem = ScholarSystems[scholarSystemValue];

  return scholarSystem.roles.map(role => ({
    value: role.value,
    label:
      role.label || (role.translationKey ? t(role.translationKey) : role.value),
  }));
}
